import styled, { css } from 'styled-components'
import { mq, fontSize, fontWeight, spacing, color } from 'core/theme'
import ButtonGroup from './ButtonGroup'

const buttonFontSize = (size: 'large' | 'medium') => {
    switch (size) {
        case 'large':
            return 'medium'
        case 'medium':
            return 'small-medium'
        default:
            return 'tiny-medium'
    }
}
const Button = styled.button.attrs(
    ({ className, size = 'medium', variant = 'default', ...props }) => {
        return {
            className: `Button${
                className ? ` ${className}` : ''
            } Button-${variant} ${buttonFontSize(size)}`
        }
    }
)`
    // variants
    ${({ variant, withoutSelectedState = false }) => {
        if (variant === 'link') {
            return css`
                color: ${({ theme }) => theme.colors.link} !important;
                padding: 0 !important;
                display: inline-block !important;
                border: none !important;
                background: none !important;
                cursor: pointer !important;
            `
        } else if (variant === 'secondary') {
            // default
            return css`
                text-wrap: nowrap;
                background: #fff;
                color: ${color('primary100')};
                cursor: pointer;
                display: flex;
                justify-content: center;
                gap: 5px;
                align-items: center;
                text-align: center;
                border: 1px solid ${color('primary100')};
                box-shadow: 0 0 0 rgba(0, 0, 0, 0);
                transition: all 300ms ease-out;

                /* text-transform: uppercase; */
                /* letter-spacing: 1.5px; */

                &,
                &:link,
                &:visited {
                    text-decoration: none;
                }

                &:hover,
                &:focus {
                    svg {
                        path {
                            color: #fff;
                        }
                        polyline {
                            color: #fff;
                        }
                    }
                }
                // sizing
                ${props => {
                    if (props.size === 'small') {
                        return css`
                            padding: ${spacing(0.2)} ${spacing(0.5)};
                        `
                    }

                    if (props.size === 'large') {
                        return css`
                            @media ${mq.small} {
                                padding: ${spacing(0.75)};
                            }

                            @media ${mq.mediumLarge} {
                                padding: ${spacing(1)};
                            }
                        `
                    }

                    return css`
                        padding: ${spacing(0.5)} ${spacing(1)};
                    `
                }}

                ${props => {
                    if (props.withoutSelectedState) {
                        return css`
                            &:hover {
                                text-decoration: none;
                                background: ${color('primary100')};
                                color: #fff;
                            }
                        `
                    } else {
                        return css`
                            &:hover,
                            &:focus {
                                text-decoration: none;
                                background: ${color('primary100')};
                                color: #fff;
                            }

                            &.Button--selected {
                                background: ${color('primary100')};
                                color: #fff;
                                cursor: default;
                                pointer-events: none;
                                border-style: solid;
                            }
                        `
                    }
                }}
            `
        } else {
            // default
            return css`
                text-wrap: nowrap;
                background: ${color('primary100')};
                color: #fff;
                cursor: pointer;
                display: flex;
                justify-content: center;
                gap: 5px;
                align-items: center;
                text-align: center;
                border: 1px solid ${color('primary100')};
                box-shadow: 0 0 0 rgba(0, 0, 0, 0);
                transition: all 300ms ease-out;

                /* text-transform: uppercase; */
                /* letter-spacing: 1.5px; */

                &,
                &:link,
                &:visited {
                    text-decoration: none;
                }

                &:hover,
                &:focus {
                    border-style: solid;
                    svg {
                        color: ${color('link')} !important;
                        path {
                            stroke: ${color('primary100')};
                            color: ${color('primary100')};
                        }
                        polyline {
                            color: ${color('primary100')};
                        }
                    }
                }
                // sizing
                ${props => {
                    if (props.size === 'small') {
                        return css`
                            padding: ${spacing(0.2)} ${spacing(0.5)};
                        `
                    }

                    if (props.size === 'large') {
                        return css`
                            @media ${mq.small} {
                                padding: ${spacing(0.75)};
                            }

                            @media ${mq.mediumLarge} {
                                padding: ${spacing(1)};
                            }
                        `
                    }

                    return css`
                        padding: ${spacing(0.5)} ${spacing(1)};
                    `
                }}

                ${props => {
                    if (props.withoutSelectedState) {
                        return css`
                            &:hover {
                                text-decoration: none;
                                background: #fff;
                                color: ${color('primary100')};
                                svg {
                                    path {
                                        stroke: ${color('primary100')};
                                        color: ${color('primary100')} !important;
                                    }
                                    polyline {
                                        color: ${color('primary100')} !important;
                                    }
                                }
                            }
                        `
                    } else {
                        return css`
                            &:hover,
                            &:focus {
                                text-decoration: none;
                                background: #fff;
                                color: ${color('primary100')};
                                svg {
                                    strok path {
                                        stroke: ${color('primary100')};
                                        color: ${color('primary100')} !important;
                                    }
                                    polyline {
                                        color: ${color('primary100')} !important;
                                    }
                                }
                            }

                            &.Button--selected {
                                background: #fff;
                                color: ${color('primary100')};
                                cursor: default;
                                pointer-events: none;
                                border-style: solid;
                            }
                        `
                    }
                }}
            `
        }
    }}

    ${ButtonGroup} & {
        /* white-space: nowrap; */
        &--active {
            cursor: default;
            pointer-events: none;
        }
        @media ${mq.small} {
            width: 100%;
            flex-basis: 100%;
            /* font-size: ${fontSize('smallest')}; */
            &:first-child {
                border-top-width: 1px;
            }

            &:hover,
            &:focus {
                border-top-color: ${color('border')};
                border-bottom-color: ${color('border')};

                &:first-child {
                    border-top-color: ${color('hover')};
                }
                &:last-child {
                    border-bottom-color: ${color('hover')};
                }
            }
        }

        @media ${mq.mediumLarge} {
            &:hover,
            &:focus {
                border-left-color: ${color('border')};
                border-right-color: ${color('border')};

                &:first-child {
                    border-left-color: ${color('hover')};
                }
                &:last-child {
                    border-right-color: ${color('hover')};
                }
            }
        }
    }
`

export default Button
